import { Article } from '@wix/answers-api';
import { pageClick } from '@wix/bi-logger-customer-care-data/v2';
import { Box, Heading, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import React, {
  FunctionComponent,
  useState,
  MouseEvent,
  KeyboardEvent,
} from 'react';

import { BI } from '../../constants';
import { DATA_HOOKS } from '../../dataHooks';
import { useBI } from '../../hooks/useBI';
import { ArticleMediumIcon } from '../../icons/ArticleMediumIcon';
import { PageType } from '../../types';
import { mapPageTypeToSourceName } from '../../utils/bi';

import css from './index.module.scss';

export type RelatedArticlesProps = {
  itemId: string;
  itemType: string;
  relatedArticles: Article[];
  pageType: PageType;
  selfArticleRedirect: (
    url: string,
    shallow?: boolean,
    targetLocale?: string
  ) => void;
};

export const RelatedArticles: FunctionComponent<RelatedArticlesProps> = ({
  itemId,
  itemType,
  relatedArticles,
  pageType,
  selfArticleRedirect,
}: RelatedArticlesProps) => {
  const { sendBIEvent } = useBI();
  const { t } = useTranslation();
  const { locale } = useRouter();
  const [hoveredRelatedArticle, setHoveredRelatedArticle] = useState('');
  const relatedArticleClick = async (
    event: MouseEvent | KeyboardEvent,
    article: Article,
    order: string
  ) => {
    const text = (event.target as HTMLElement).textContent as string;
    await sendBIEvent(
      pageClick({
        source_name: mapPageTypeToSourceName(pageType),
        kb_lang: locale as string,
        item_id: itemId,
        item_type: itemType,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.RELATED_CONTENT,
        clicked_url: `/${locale}${article.uri}`,
        clicked_text: text,
        clicked_item_order: order,
        clicked_item_id: article.id,
        is_page_content_click: false,
      })
    );
  };

  return relatedArticles && relatedArticles.length > 0 ? (
    <Box
      className={classNames(css.wrapper, css.wrapperNew)}
      direction="vertical"
    >
      <Heading size="medium" as="h2" className={css.title}>
        {t('related-content.title')}
      </Heading>
      <Box direction="horizontal" className={css.relatedArticles}>
        {relatedArticles.map((relatedArticle, index) => (
          <div
            key={relatedArticle.id}
            onMouseEnter={() => setHoveredRelatedArticle(relatedArticle.id)}
            onMouseLeave={() => setHoveredRelatedArticle('')}
            className={css.relatedArticle}
            data-hook={DATA_HOOKS.RELATED_ARTICLE_LINK}
            data-href={`/${locale}${relatedArticle.uri}`}
            onClick={async (e) => {
              await relatedArticleClick(e, relatedArticle, `${index + 1}`);
              selfArticleRedirect(
                `/${locale}${relatedArticle.uri}`,
                false,
                relatedArticle.locale
              );
            }}
          >
            <Box gap="6px" verticalAlign="top">
              <ArticleMediumIcon />
              <Box className={css.relatedArticleTitleWrapper}>
                <Text
                  size="small"
                  disabled
                  className={`${css.relatedArticleTitle} ${
                    hoveredRelatedArticle === relatedArticle.id &&
                    css.relatedArticleTitleHovered
                  }`}
                  dataHook={DATA_HOOKS.RELATED_ARTICLE_TEXT}
                >
                  {relatedArticle.title}
                </Text>
              </Box>
            </Box>
          </div>
        ))}
      </Box>
    </Box>
  ) : null;
};
